import { LitElement } from 'lit';

export class AccordionItem extends LitElement {
  static properties = {
    title: { type: String },
    open: { type: Boolean, reflect: true },
    accordionStyle: { type: String, attribute: 'accordion-style' }
  };

  createRenderRoot() {
    return this; // Use light DOM instead of shadow DOM
  }

  constructor() {
    super();
    this.title = '';
    this.open = false;
    this.accordionStyle = 'normal';
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Find the button that PHP generated and add click listener
    const button = this.querySelector('.accordion-item-header');
    if (button) {
      button.addEventListener('click', () => this.toggle());
    }
    
    // Initialize state after DOM is ready
    setTimeout(() => this.initializeState(), 0);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === 'open' && oldValue !== null) {
      this.initializeState();
    }
  }

  initializeState() {
    const content = this.querySelector('.accordion-item-content');
    const icon = this.querySelector('.accordion-icon');
    const bgWrapper = this.querySelector('.accordion-bg-wrapper');

    if (!content || !icon) return;

    // Clear any transforms from content images
    const contentImages = this.querySelectorAll('.accordion-item-content img');
    contentImages.forEach(img => {
      if (window.gsap) {
        window.gsap.set(img, { rotation: 0, clearProps: 'transform' });
      } else {
        img.style.transform = 'none';
      }
    });

    // Set initial state based on open property
    if (!this.open) {
      if (window.gsap) {
        window.gsap.set(content, { height: 0, opacity: 0 });
        window.gsap.set(icon, { rotation: 0 });
        if (bgWrapper) window.gsap.set(bgWrapper, { opacity: 0 });
      } else {
        content.style.height = '0px';
        content.style.opacity = '0';
        content.style.overflow = 'hidden';
        icon.style.transform = 'rotate(0deg)';
        if (bgWrapper) bgWrapper.style.opacity = '0';
      }
    } else {
      // For initially open items, set height via plain CSS 'auto' rather than
      // gsap.set (which resolves 'auto' to a one-time pixel snapshot). Content
      // like images may still be loading, so a literal 'auto' keeps the box
      // fluid as they finish loading instead of clipping at a stale height.
      content.style.height = 'auto';
      content.style.opacity = '1';
      content.style.overflow = 'visible';
      if (window.gsap) {
        window.gsap.set(icon, { rotation: 45 });
        if (bgWrapper) window.gsap.set(bgWrapper, { opacity: 1 });
      } else {
        icon.style.transform = 'rotate(45deg)';
        if (bgWrapper) bgWrapper.style.opacity = '1';
      }
    }
  }

  toggle() {
    this.open = !this.open;
    this.animateContent();
  }

  animateContent() {
    const content = this.querySelector('.accordion-item-content');
    const icon = this.querySelector('.accordion-icon');
    const bgWrapper = this.querySelector('.accordion-bg-wrapper');

    if (!content || !icon) return;

    if (window.gsap) {
      // Use GSAP for smooth animations
      if (this.open) {
        const naturalHeight = content.scrollHeight;

        const tl = window.gsap.timeline({
          onComplete: () => {
            // Content (e.g. lazy-loading images) may still grow after the
            // tween ends. Release the pixel height back to 'auto' so the
            // box keeps expanding instead of clipping at the snapshot height.
            content.style.height = 'auto';
            content.style.overflow = 'visible';
          }
        });
        tl.to(content, { height: naturalHeight, opacity: 1, duration: 0.3, ease: 'power2.out' })
          .to(icon, { rotation: 45, duration: 0.2, ease: 'power2.out' }, 0);

        if (bgWrapper) {
          tl.to(bgWrapper, { opacity: 1, duration: 0.3, ease: 'power2.out' }, 0);
        }
      } else {
        // Before closing, get the current height and set it explicitly
        // This allows smooth animation from current height to 0 (instead of instant snap from 'auto')
        const currentHeight = content.scrollHeight;
        
        const tl = window.gsap.timeline();
        // First set the explicit height (no animation)
        tl.set(content, { height: currentHeight })
          // Then animate to closed
          .to(content, { height: 0, opacity: 0, duration: 0.3, ease: 'power2.out' })
          .to(icon, { rotation: 0, duration: 0.2, ease: 'power2.out' }, 0);
        
        if (bgWrapper) {
          tl.to(bgWrapper, { opacity: 0, duration: 0.3, ease: 'power2.out' }, 0);
        }
      }
    } else {
      // Fallback: Use CSS transitions
      content.style.transition = 'height 0.3s ease, opacity 0.3s ease';
      icon.style.transition = 'transform 0.2s ease';
      if (bgWrapper) bgWrapper.style.transition = 'opacity 0.3s ease';

      if (this.open) {
        // First get the natural height
        content.style.height = 'auto';
        const naturalHeight = content.scrollHeight;
        
        // Set it back to current state then animate
        content.style.height = '0px';
        content.style.opacity = '0';
        
        // Force reflow
        content.offsetHeight;
        
        // Now animate to open
        content.style.height = naturalHeight + 'px';
        content.style.opacity = '1';
        icon.style.transform = 'rotate(45deg)';
        if (bgWrapper) bgWrapper.style.opacity = '1';
        
        // After animation, set height to auto for responsive behavior
        setTimeout(() => {
          content.style.height = 'auto';
          content.style.overflow = 'visible';
        }, 300);
      } else {
        // Get current height first
        const currentHeight = content.scrollHeight;
        content.style.height = currentHeight + 'px';
        content.style.overflow = 'hidden';
        
        // Force reflow
        content.offsetHeight;
        
        // Now animate to closed
        content.style.height = '0px';
        content.style.opacity = '0';
        icon.style.transform = 'rotate(0deg)';
        if (bgWrapper) bgWrapper.style.opacity = '0';
      }
      
      // Remove transitions after animation to prevent issues
      setTimeout(() => {
        content.style.transition = '';
        icon.style.transition = '';
      }, 350);
    }
  }
}
